"use client";

import { useTranslations } from "next-intl";
import { Check, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export interface RegionDetailPanelProps {
	currentChannel: string;
	onBack: () => void;
	onSelect: (channel: "eu" | "us") => void;
}

const regions: ReadonlyArray<{
	channel: "eu" | "us";
	icon: string;
	titleKey: "regionWorldwide" | "regionAmericas";
	subKey: "regionWorldwideSub" | "regionAmericasSub";
}> = [
	{ channel: "eu", icon: "🌍", titleKey: "regionWorldwide", subKey: "regionWorldwideSub" },
	{ channel: "us", icon: "🌎", titleKey: "regionAmericas", subKey: "regionAmericasSub" },
];

export function RegionDetailPanel({ currentChannel, onBack, onSelect }: RegionDetailPanelProps) {
	const t = useTranslations("localeChannel");
	return (
		<div className="flex h-full flex-col">
			<div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted-soft py-3 pl-4 pr-16">
				<button
					type="button"
					onClick={onBack}
					aria-label={t("back")}
					className="-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<h3 className="text-sm font-semibold text-foreground">{t("regionLabel")}</h3>
			</div>

			<div className="flex flex-col gap-3 px-5 py-5">
				{regions.map((region) => {
					const isActive = region.channel === currentChannel;
					return (
						<button
							key={region.channel}
							type="button"
							aria-current={isActive ? "true" : undefined}
							onClick={() => onSelect(region.channel)}
							className={cn(
								"flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors",
								isActive
									? "border-primary bg-primary text-primary-foreground shadow-sm"
									: "hover:bg-muted/40 border-border bg-background",
							)}
						>
							<span className="text-2xl leading-none" aria-hidden>
								{region.icon}
							</span>
							<div className="min-w-0 flex-1">
								<div className="flex items-center justify-between gap-2">
									<span className="text-sm font-semibold">{t(region.titleKey)}</span>
									{isActive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
								</div>
								<p
									className={cn(
										"mt-1 text-xs leading-relaxed",
										isActive ? "text-primary-foreground/80" : "text-muted-foreground",
									)}
								>
									{t(region.subKey)}
								</p>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}
